/**
 * Auth client — wraps Better Auth REST endpoints
 *
 * Worker base URL (where /api/auth/* lives) — ตั้งใน .env.production:
 *   VITE_API_URL=https://cher-fb-bot.nayaa.workers.dev
 */

const BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ?? window.location.origin;

export interface SessionUser {
  id: string;
  email: string;
  name?: string;
  image?: string;
  role?: string;
}

export interface SessionData {
  user: SessionUser;
  session: { id: string; expiresAt: string };
}

/**
 * GET /api/auth/get-session — return session or null
 * ใช้ Better Auth standard endpoint
 */
export async function getSession(): Promise<SessionData | null> {
  const res = await fetch(`${BASE}/api/auth/get-session`, {
    credentials: 'include',
  });
  if (!res.ok) return null;
  const data = await res.json().catch(() => null);
  if (!data || !data.user) return null;
  return data as SessionData;
}

/**
 * Trigger Google OAuth — POST → ได้ redirect URL → ตามไป
 * Better Auth standard: POST /api/auth/sign-in/social { provider, callbackURL }
 */
export async function signInWithGoogle(callbackURL?: string): Promise<void> {
  const res = await fetch(`${BASE}/api/auth/sign-in/social`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      provider: 'google',
      callbackURL: callbackURL ?? window.location.origin,
    }),
  });
  if (!res.ok) {
    throw new Error(`sign-in failed: ${res.status}`);
  }
  const data = (await res.json().catch(() => null)) as { url?: string } | null;
  if (data?.url) {
    window.location.href = data.url;
  } else {
    throw new Error('sign-in: no redirect URL returned');
  }
}

/**
 * POST /api/auth/sign-out — clear session cookie
 */
export async function signOut(): Promise<void> {
  await fetch(`${BASE}/api/auth/sign-out`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({}),
  });
  // Force reload to clear in-memory session state
  window.location.href = '/';
}
